import React, { useEffect, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { partnerApi } from "../../../../../../../../../../services/Api/Partner/partnerApi";
import { LoanApi, OrganizationApi } from "../../../../../../../../../../services/Api/types";
import { selectPartner } from "../../../../../../../../../../services/partner/selectors";
import { selectUser } from "../../../../../../../../../../services/users/selectors";
import LoanForm from "../../../../../../../../components/Loan/LoanForm";
import LoanMissingData from "../../../../../../../../components/Loan/LoanMissingData";
import LoanPreview from "../../../../../../../../components/Loan/LoanPreview/LoanPreview";
import NewLoanPreview from "../../../../../../../../components/Loan/NewLoanPreview/NewLoanPreview";
import SelectNewLoanMode from "../../../../../../../../components/Loan/SelectNewLoanMode";

interface StateType {
	from: { pathname: string };
	organization?: OrganizationApi;
}

const PartnerPreviewLoansNewPage = () => {
	const { t } = useTranslation("admin");
	const [loading, setLoading] = useState(false);
	const { state } = useLocation<StateType>();
	const [organization, setOrganization] = useState<OrganizationApi | undefined>(undefined);
	const urlSearchParams = new URLSearchParams(window.location.search);
	const organizationId = urlSearchParams.get("organization_id");
	const devices = urlSearchParams.getAll("devices");
	const refId = urlSearchParams.get("reference_id");
	const [loanExist, setLoanExist] = useState(false);
	const redirectUri = urlSearchParams.get("return_url");
	const partner = useSelector(selectPartner);
	const user = useSelector(selectUser);
	const history = useHistory();
	const [formType, setFormType] = useState<undefined | string>(
		state?.from && organizationId && (refId || devices) ? "POSNET" : undefined
	);
	const [loan, setLoan] = useState<undefined | any>(undefined);
	const { addFlash, addSuccessFlash } = useFlash();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const isLoanFulfilled = loan && loan.reference_id && loan.organization_id && loan.items;

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.loan.header.title")} | ${t("modules.partner.field.partner_panel.title")}`);
		handleInit();
	}, []);

	const handleInit = async () => {
		try {
			const organizationRes = await partnerApi.getPartnerOrganization(
				Number(partner?.id),
				Number(organizationId)
			);
			setOrganization(organizationRes);
			const loanData = {
				organization_id: organizationId,
				organization: organizationRes,
				items: devices
					.filter((device) => device.includes("|"))
					.map((num) => {
						return {
							name: num.split("|")[0],
							serial_id: num.split("|")[1],
						};
					}),
			};

			if (refId) {
				const loanRes = await partnerApi.findLoanByReferenceId(Number(partner?.id), refId, {
					include: "organization,items,partner",
				});
				if (loanRes) {
					checkIfLoanHasSameParameters(loanRes);
				}
				if (loanRes) {
					setLoanExist(true);
				}

				loanRes
					? setLoan({
							...loanRes,
							...loanData,
					  })
					: setLoan({
							reference_id: refId,
							...loanData,
					  });
			} else {
				setLoan({
					reference_id: refId,
					...loanData,
				});
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const checkIfLoanHasSameParameters = (loan: LoanApi) => {
		const loanDevices = loan?.items?.map((item) => `${item.name}|${item.serial_id}`);
		if (
			JSON.stringify(loanDevices) === JSON.stringify(devices) &&
			Number(organizationId) === loan.organization.id &&
			refId === loan.reference_id &&
			loan.status === "DRAFT"
		)
			history.push(`/partnership/${partner?.id}/loans/${loan.id}`);
	};

	const checkOrganizations = () => {
		if (!organization) {
			return (
				<Alert variant={"danger"}>
					Brak dostępu do lokalizacji.&nbsp;
					{redirectUri && <a href={redirectUri}>{t("common.action.back", { ns: "lib" })}</a>}
				</Alert>
			);
		}
		return null;
	};

	const checkLoan = () => {
		if (loan === null) {
			return (
				<Alert variant={"danger"}>
					Brak dostępu do umowy.&nbsp;
					{redirectUri && <a href={redirectUri}>{t("common.action.back", { ns: "lib" })}</a>}
				</Alert>
			);
		}
	};

	const goBack = () => {
		history.push(`/partnership/${partner?.id}/loans`);
	};

	if (!loan && loan !== null) {
		return <LoadingContainer />;
	}

	const overrideLoan = async () => {
		setLoading(true);
		partnerApi
			.updateLoan(Number(partner?.id), loan)
			.then((res) => {
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
				history.push(`/partnership/${partner?.id}/loans/${res.id}`);
			})
			.catch((err) => {
				handleError.alert(err, addFlash);
			});
	};

	if (loan && loan.id && loanExist) {
		return (
			<>
				<Header
					title={t("modules.loan.header.title")}
					back={() => {
						if (redirectUri) goBack();
					}}
				/>
				<div className={"content-preview"}>
					{loan.status === "DRAFT" && (
						<div>
							<Alert variant={"warning"}>
								<Link to={`/partnership/${partner?.id}/loans/${loan.id}`}>
									Istnieje już taka umowa. Kliknij żeby przejść
								</Link>
							</Alert>
							<LoanPreview loan={loan} handleUpdateLoan={setLoan} />
							<Form.Group className="form-group">
								<ButtonLoading loading={loading} onClick={overrideLoan}>
									Nadpisz umowę
								</ButtonLoading>
							</Form.Group>
						</div>
					)}
					{loan.status !== "DRAFT" && (
						<>
							<Alert variant={"danger"}>Istnieje już taka umowa i nie można jej edytować</Alert>
							<Link className="btn btn-primary" to={`/partnership/${partner?.id}/loans/${loan.id}`}>
								Przekieruj do umowy
							</Link>
						</>
					)}
				</div>
			</>
		);
	}

	if (loan && loan.id && !loanExist) {
		return <Redirect to={`/partnership/${partner?.id}/loans/${loan.id}`} />;
	}

	return (
		<>
			<Header title={t("modules.loan.header.title")} back={goBack} />
			<div className={"content-preview"}>
				{checkOrganizations()}
				{checkLoan()}
			</div>
			{loan && organization && (
				<>
					{formType === "GOPOS" && (
						<LoanForm
							organization={organization}
							handleUpdate={setLoan}
							loan={{ ...loan, type: "GOPOS" }}
							user={user}
						/>
					)}
					{formType === "POSNET" && (
						<>
							{isLoanFulfilled ? (
								<NewLoanPreview loan={{ ...loan, type: "POSNET" }} organization={organization} />
							) : (
								<LoanMissingData redirectUri={redirectUri} loan={{ ...loan, type: "POSNET" }} />
							)}
						</>
					)}
					{formType === undefined && (
						<SelectNewLoanMode handleUpdateFormType={setFormType} organization={organization} />
					)}
				</>
			)}
		</>
	);
};
export default PartnerPreviewLoansNewPage;
